import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import Model from 'flarum/common/Model';
import attachMenu from './attachMenu';
import SchedulerIndexPage from './components/SchedulerIndexPage';
import SchedulerLessonNotification from './components/SchedulerLessonNotification';
import Lesson from './models/Lesson';
// v0.19.14 — iOS「modal 顶部露出页面内容」的真机诊断探针。
//   ⛔ **默认关** (URL 参数 `?zskbdiag=1` 才激活) ⇒ 没带参数时不进 DOM、不挂监听、不写样式。
//   ⚠️ 它是**全局**的 (要在「没有 picker 的页面」上也能开) ⇒ 挂在扩展的 initializer 上。
import { initIosKeyboardDiag } from './utils/iosKeyboardDiag';
export { default as extend } from './extend';

// FullCalendar + flatpickr vendor assets 加载 (r.1 FAIL CRITICAL 2 修复)
//   不用 <script async> 链式 onload (浏览器对 async=false onload 时机不可靠, 插件引用 FullCalendar.X 早于 core 加载完成),
//   改用同步 fetch + eval, 保证执行顺序
// r.1 FAIL MINOR 3 修复: vendor flatpickr 全局变量 + setInterval 等 ready

const VENDOR_BASE = '/assets/extensions/ziiven-ziven-scheduler';
const FC_BASE = VENDOR_BASE + '/fullcalendar';
async function loadScriptText(url) {
  const r = await fetch(url, {
    credentials: 'omit'
  });
  if (!r.ok) throw new Error('Failed to load ' + url + ': ' + r.status);
  return await r.text();
}
function loadScriptsSequential(urls) {
  // XHR 同步 fetch + <script> 文本注入 — 唯一可靠方式 (var 在 script 标签里挂到 window)
  for (const url of urls) {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, false);
      xhr.send();
      if (xhr.status !== 200 && xhr.status !== 0) {
        console.warn('[ziven-scheduler] skip', url, 'status', xhr.status);
        continue;
      }
      // 把脚本内容写进 <script> 标签 — script 标签内 var 永远挂到 window (非 strict eval 那种 local)
      const s = document.createElement('script');
      s.charset = 'utf-8';
      s.dataset.zs = url;
      s.text = xhr.responseText;
      document.head.appendChild(s);
      // v0.8.10 — 删了 loading/loaded 两行 trace 日志 (辉哥 10/7 拍板: 清 console 噪声)
      //   同一循环里的 warn (非 200) 与 error (加载失败) 保留 ——
      //   那是"脚本到底有没有加载上"的真实诊断依据, 删了等于蒙眼。
    } catch (e) {
      console.error('[ziven-scheduler] failed to load', url, e);
    }
  }
}

// 全局暴露 ziven-scheduler asset base 给 CalendarTab / BindCardModal 使用
window.__zivenSchedulerAssetBase = VENDOR_BASE;
app.initializers.add('ziven-scheduler', () => {
  // v0.19.14 — 装诊断探针 (⛔ 没带 `?zskbdiag=1` 时这一句是**空操作**:
  //   不进 DOM、不挂监听、不写样式、`window.__zsKbDiag` 连定义都不定义)
  initIosKeyboardDiag();
  // v0.8.10 — 删了 '[zs-v050j-fix] start' trace marker (辉哥 10/7 拍板: 清 console 噪声)
  //   这行是 v0.5.0j-r2 当年二次验收时的一次性 trace, 修完就没意义了。

  // v0.6.53 (辉哥 9/30 16:37+ 拍板) — 8 个 permission attribute (Flarum 2.0 UserSerializer 暴露)
  // v0.6.56 (辉哥 9/30 18:20+ 拍板) — 加 2 个 → 总 10 个 (canViewNotifications + canViewLogs)
  // v0.17.32 (辉哥 10/14 拍板) — 减 1 个 → 当时总 9 个 (canViewNotifications 随自建通知中心下线)
  // v0.17.77 (辉哥 10/16 拍板) — 加 2 个 → 当时总 11 个 (canEditBookedLessons / canCancelBookedLessons)
  // v0.17.78 (辉哥 10/17 拍板) — 加 1 个 → 当时总 12 个 (canCheckInLessons)
  // v0.17.80 (辉哥 10/17 拍板) — 加 1 个 → 当时总 13 个 (canViewMyCards)
  // v0.18.0  (辉哥 10/18 拍板) — 加 1 个 → 当时总 14 个 (canReviewLessons)
  // v0.18.32 (辉哥 10/20 拍板) — 加 1 个 → 当时总 15 个 (canManageSchools)
  // v0.18.45 (辉哥 10/21 拍板) — 加 2 个 → 现总 17 个 (canViewDashboard + canViewSchools)
  //   extend.php 走 Extend\ApiResource(UserResource::class)->fields(UserSchedulerPermissions::class)
  //   跟 ziven-calendar 暴露 canViewEvents / canModerateEvents / canStartEvents 同款 pattern
  //   前端 getter: user.canView() / user.canManageCards() ... 调用返 boolean
  app.store.models.users.prototype.canView = Model.attribute('canView');
  app.store.models.users.prototype.canManageCards = Model.attribute('canManageCards');
  app.store.models.users.prototype.canManageStudents = Model.attribute('canManageStudents');
  app.store.models.users.prototype.canManageRooms = Model.attribute('canManageRooms');
  // v0.18.32 (辉哥 10/20 拍板) — 「校区管理」拆成独立的 getter (第 15 个权限位)。
  //   ⚠️ canManageRooms **不动** (仍管教室 5 个端点) —— 拆的是校区那 6 个端点。
  //   ⚠️ 后端同名 attribute 见 src/Api/Attributes/UserSchedulerPermissions.php 的 canManageSchools。
  //   ⚠️ 消费点: SchedulerIndexPage.TAB_PERMISSIONS 的 `schools` 项 (「校区」tab 的门)。
  app.store.models.users.prototype.canManageSchools = Model.attribute('canManageSchools');

  // v0.18.45 (辉哥 10/21 拍板) — 「仪表盘」+ 「校区只读」两个 getter (第 16 / 17 个权限位)。
  //   ⚠️ canViewDashboard: 「仪表盘」tab 的门。**全员可见** (迁移把 viewDashboard 发给
  //      持有任一 scheduler.* 位的组, 排除 Admin 组) ⇒ 4 种角色都能进排课中心首页。
  //      响应形状按角色不同 (店长看经营 / 教练看课时 / 学员看剩余课时), 敏感字段在**服务端**
  //      就被裁掉了 (不是前端遮挡) —— 见后端 Dashboard\OverviewController 的类注释。
  //   ⚠️ canViewSchools: 「校区」tab 的**只读**门。取代 v0.18.32 之前的 canManageSchools。
  //      「写位蕴含读位」由后端迁移保证 (持有 manageSchools 的组也发 viewSchools)。
  //   跟上面 15 个同口径: 取的是 actor(当前登录者自己), 只在 app.session.user 上读。
  app.store.models.users.prototype.canViewDashboard = Model.attribute('canViewDashboard');
  app.store.models.users.prototype.canViewSchools = Model.attribute('canViewSchools');
  app.store.models.users.prototype.canBookLessons = Model.attribute('canBookLessons');
  app.store.models.users.prototype.canViewLessons = Model.attribute('canViewLessons');
  app.store.models.users.prototype.canManageLessons = Model.attribute('canManageLessons');
  app.store.models.users.prototype.canViewStats = Model.attribute('canViewStats');
  // v0.17.32 — canViewNotifications 已删 (自建通知中心下线)。同批的 canViewLogs 保留,
  //   它对应操作日志 tab, 与 v0.6.56 的 viewLogs 成对, 不能连坐。
  app.store.models.users.prototype.canViewLogs = Model.attribute('canViewLogs');

  // v0.17.77 (辉哥 10/16 拍板) — 「约课 / 编辑已约课程 / 取消约课」三件套的后两位 getter。
  //   「约课」复用上面已有的 canBookLessons, 不新增第三个 getter。
  //   ⚠️ 这两个位**不是** canManageLessons 的同义词 —— manageLessons 是重权限位, 还额外守着
  //      课表版本 / 固定课模板 / 时段预留 (爆炸半径见 PERMISSIONS.md)。前端两个页面的判据:
  //        CalendarTab.openSlotMenu : 「修改约课」认 edit||manage, 「取消约课」认 cancel||manage,
  //                                    「预留/固定课」只认 manage。
  //        LessonsTab               : 「+ 新建预约」认 book||manage, 「消课」认 manage,
  //                                    「签到」认 edit||manage (学员靠它签到, 后端只收窄到
  //                                    「自己名下 + status 白名单」, 不再必然 403)。
  //   ⚠️ 跟上面 10 个同口径: 取的是 **actor**(当前登录者自己), 只在 app.session.user 上读。
  app.store.models.users.prototype.canEditBookedLessons = Model.attribute('canEditBookedLessons');
  app.store.models.users.prototype.canCancelBookedLessons = Model.attribute('canCancelBookedLessons');

  // v0.17.78 (辉哥 10/17 拍板) — 「学员签到」getter (第 12 个权限位)。
  //   ⚠️ 独立成位**不是**多余的: 辉哥否决过「给学员勾 canEditBookedLessons 来实现签到」
  //      (原话: 不能让学员拿到"编辑已约课程"权限)。学员只勾这一位就能签到。
  //   爆炸半径 = 只有 UpdateLessonController 的「纯 status=checked-in」那一档。
  //   ⚠️ **不等于**「能消课」: 后端 completed 要 edit 位, 学员那侧 status 白名单也没有 completed。
  //      LessonsTab.canCompleteLesson() 刻意只认 canManageLessons, 前端后端一起挡。
  //   跟上面 11 个同口径: 取的是 actor(当前登录者自己), 只在 app.session.user 上读。
  app.store.models.users.prototype.canCheckInLessons = Model.attribute('canCheckInLessons');

  // v0.17.80 (辉哥 10/17 拍板) — 「我的卡片」getter (第 13 个权限位)。
  //   ⚠️ 只勾**学员组**, Mod (group 4) 拿不到 ⇒ tab 栏不显示这个 tab, 直连 URL 也被后端 403。
  //   爆炸半径 = 一个只读端点 (GET /ziiven/scheduler/my-cards), 且**行级锁死 actor.id**
  //   —— 不接受 ?student_id= 传别人的 id, 连「非学员放宽」的角色例外都没有 (见该控制器类注释)。
  //   跟上面 12 个同口径: 取的是 actor(当前登录者自己), 只在 app.session.user 上读。
  app.store.models.users.prototype.canViewMyCards = Model.attribute('canViewMyCards');

  // v0.18.0 (辉哥 10/18 拍板) — 「写课评」getter (第 14 个权限位)。
  //   爆炸半径 = SaveLessonReviewController 一个写端点 (+ manageLessons 本来就能写)。
  //   跟上面 13 个同口径: 取的是 actor(当前登录者自己), 只在 app.session.user 上读。
  app.store.models.users.prototype.canReviewLessons = Model.attribute('canReviewLessons');

  // v0.18.80 (辉哥 10/24 拍板) — 「退费管理」getter (第 18 个权限位)。
  //   ⚠️ 独立成位**不是**多余的: 退费记录**直接显示金额** (实退额 / 系统核算额 / 差额),
  //      可见面比操作日志 (只显示描述文案) 窄得多 ⇒ 不能复用 `canViewLogs`。
  //      也不复用 `canManageStudents` (那是**发起**退费的写位)。
  //   爆炸半径 = 只有 GET /ziiven/scheduler/refunds 一个**只读**端点 (+ 校区范围收窄)。
  //   ⚠️ **审批权不在这里** —— 审批权沿用既有财务组 (scheduler-role-finance-group.group),
  //      由端点下发的 `can_approve` 决定渲不渲染「通过 / 驳回」按钮 ⇒ 前端**不判**审批权。
  //   跟上面 17 个同口径: 取的是 actor(当前登录者自己), 只在 app.session.user 上读。
  app.store.models.users.prototype.canViewRefunds = Model.attribute('canViewRefunds');

  // v0.17.76 (辉哥 10/16 拍板) — 「我是不是学员」。
  //   ⚠️ 这**不是**第 10 个权限位, 是从 RoleResolver 派生的布尔标记 (后端见
  //   Api/Attributes/UserSchedulerPermissions::isStudent)。后台权限矩阵里没有它。
  //   ⚠️ 取的是 **actor**(当前登录者自己), 不是 model 那个 user ——
  //   所以只在 app.session.user 上读, 拿它判断别人是错的。
  //   ⚠️ 判据在后端是 `RoleResolver::resolve() === 'student'` 严格相等;
  //      绝不能改成 SchoolContext::detectRole() (它 fallback 成 student, 会把 Mod 判成学员)。
  app.store.models.users.prototype.isStudent = Model.attribute('isStudent');

  // 1) FullCalendar plugins 按依赖顺序加载
  //    core → premium-common → resource → daygrid → timegrid → resource-daygrid → resource-timegrid → interaction
  //    (每个 plugin 依赖 core + 它前面加载的 plugin, DayGrid.Internal 是 timegrid 的硬依赖)
  // v0.6.43a — 加 interaction plugin (辉哥 9/30 9:27+ 反馈, 拖拽不动 root cause)
  //   vendor file: /var/www/bjxy-website/public/assets/extensions/ziiven-ziven-scheduler/fullcalendar/interaction.global.min.js
  //   35KB v6.1.21 (跟其他 7 个 plugin 同版本, 跟 index.global.min.js 6.1.21 一致)
  //   必须在 resource-timegrid 后加载 (interaction 依赖 FC core 跟 timegrid 注册的事件系统)
  loadScriptsSequential([`${FC_BASE}/index.global.min.js`, `${FC_BASE}/premium-common.global.min.js`, `${FC_BASE}/resource.global.min.js`, `${FC_BASE}/daygrid.global.min.js`, `${FC_BASE}/timegrid.global.min.js`, `${FC_BASE}/resource-daygrid.global.min.js`, `${FC_BASE}/resource-timegrid.global.min.js`, `${FC_BASE}/interaction.global.min.js`,
  // v0.7.8p — scrollgrid plugin (辉哥 10/5 拍板 B 方案)
  //   dayMinWidth 会让 resource-timegrid 走 renderHScrollLayout, 该方法第一行要求
  //   context.pluginHooks.scrollGridImpl, 缺它直接抛 'No ScrollGrid implementation' 白屏。
  //   官方 @fullcalendar/scrollgrid@6.1.21 本体零 license 逻辑 (license 校验在 premium-common),
  //   跟现有 vendor 同版本 6.1.21 (premium-common md5 已核对一致)。
  //   必须在 premium-common 之后加载 (它 deps 引用 premium-common)。
  `${FC_BASE}/scrollgrid.global.min.js`]);

  // 2) flatpickr (ziven-core vendor 全局函数) + zh locale
  //   v0.6.12a — 恢复 zh.js 加载 (SOP 108 真视觉对比 PASS)
  //   v0.6.12 错位: 删 zh.js 加载 + 强制 locale 'default' + override l10ns.default 改英文,
  //   但 BookingModal 实际 (V 测 puppeteer 截图实测) 是中文 ("九月 2026" + "周日 周一 周二...").
  //   v0.6.12a 修: 恢复 zh.js 加载, locale zh fallback, 跟 BookingModal 真视觉同款.
  //   SOP 108 强化 (辉哥 9/27 18:30 拍板): "同款" 任务不能假设 user 描述 = 实测视觉.
  //   BookingModal 实际加载的是 ziven-core vendor 定制 flatpickr (l10ns.zh=中文, l10ns.default=中文),
  //   跟 upstream flatpickr (英文默认) 不一样. v0.6.12 没真截图对比就走错方向.
  loadScriptsSequential(['/assets/extensions/ziiven-ziven-core/flatpickr/flatpickr.js', '/assets/extensions/ziiven-ziven-core/flatpickr/zh.js',
  // v0.6.19 — monthSelect plugin (辉哥 9/28 14:35 拍板方案 A: 12 月网格 month-only)
  //   vendor flatpickr core 不带 monthSelect plugin (ziven-lesson-feeback 同款 v4.6.13 有),
  //   从 ziven-lesson-feeback js/node_modules 拷贝到 ziiven-ziven-core/flatpickr/plugins/monthSelect/,
  //   UMD 全局名 monthSelectPlugin. 必须在 flatpickr.js 之后加载 (依赖 FlatPickr class).
  '/assets/extensions/ziiven-ziven-core/flatpickr/plugins/monthSelect/index.js']);

  // 3) flatpickr.min.css + monthSelect style
  if (!document.querySelector('link[data-zs-flatpickr]')) {
    const l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = '/assets/extensions/ziiven-ziven-core/flatpickr/flatpickr.min.css';
    l.dataset.zsFlatpickr = '1';
    document.head.appendChild(l);
  }
  // v0.6.19 — monthSelect plugin 自带 style.css (12 月网格布局), vendor flatpickr.min.css 不带
  if (!document.querySelector('link[data-zs-flatpickr-monthselect]')) {
    const l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = '/assets/extensions/ziiven-ziven-core/flatpickr/plugins/monthSelect/style.css';
    l.dataset.zsFlatpickrMonthselect = '1';
    document.head.appendChild(l);
  }

  // 4) 路由注册
  // v0.17.31 — 注册 Flarum 原生铃铛通知组件 (辉哥 10/14 拍板)
  //   key 必须跟 Blueprint::getType() 逐字一致: 铃铛渲染时是拿
  //   `notification.contentType()` 做 key 去 app.notificationComponents 里查组件
  //   (ForumApplication 的 notificationComponents + NotificationType.tsx), 对不上就整条不渲染。
  //   4 个 type 共用一个组件, 组件内部按 contentType 分支出文案和图标。
  app.notificationComponents.schedulerLessonBooked = SchedulerLessonNotification;
  app.notificationComponents.schedulerLessonRescheduled = SchedulerLessonNotification;
  app.notificationComponents.schedulerLessonCancelled = SchedulerLessonNotification;
  app.notificationComponents.schedulerLessonRestored = SchedulerLessonNotification;
  // v0.18.0 (辉哥 10/18 拍板) — 课评通知 (第 5 个 type, 复用同一个组件)
  //   ⛔⛔ 注册在这里**不代表**「每次保存课评都会发通知」: 投递时机由后端
  //      SaveLessonReviewController 的 isFirstPublish() 把关 (R3: 只在 draft → published 那一次发)。
  app.notificationComponents.schedulerLessonReviewed = SchedulerLessonNotification;

  //   v0.17.31 — 给通知的 subject (课次) 注册前端 model。
  //   key 必须与后端 LessonResource::type() **逐字一致** (`schedulerLessons`):
  //   Store.pushObject() 靠 data.type 查这张表, 对不上就抛
  //   "Pushing object of type ... not allowed", 整批通知入不了 store, 铃铛全空。
  //   后端 ApiResource 与这里是一对, 少任何一半通知都显示不出来。
  app.store.models.schedulerLessons = Lesson;

  //   通知中心整页 (NotificationGrid) 的「通知类型分组」。少这一段的话:
  //   铃铛里能收到通知, 但通知中心那一页没有这几个类型的开关行, 用户无法在整页里管理。
  //
  //   ⚠️ 必须**异步**加载 NotificationGrid, 不能 import 静态引入:
  //     Flarum 2.0 把 NotificationGrid 打成了 lazy chunk (实测:
  //     flarum.reg.get('core','forum/components/NotificationGrid') 返回 undefined,
  //     但它在 chunkModules 里带 `core:forum/components/NotificationGrid` 条目),
  //     静态 import 拿到的是 undefined, 后面 .prototype 直接炸。
  //     ExportRegistry 暴露的 asyncModuleImport() 正是为这种延迟模块准备的。
  //
  //   这段刻意**不 await**, 也整段包 try/catch: 通知中心整页的分组是锦上添花,
  //   铃铛本身已经在上面同步注册好了。它加载失败绝不能影响铃铛。
  //
  //   ⚠️ 必须等 app.forum 就绪之后再触发: ExportRegistry.versionedChunkUrl() 会读
  //     `app.forum.attribute('jsChunksBaseUrl')` 来拼 chunk 地址。在 initializer 里
  //     直接调 asyncModuleImport() 会早于 forum 数据装载, 实测抛
  //     "Cannot read properties of undefined (reading 'attribute')",
  //     且失败发生在 chunk 加载器内部, 会连带影响整页其余延迟模块的加载。
  //     setTimeout(0) 让它排到当前初始化批次之后, 此时 app.forum 已可用。
  setTimeout(() => {
    flarum.reg.asyncModuleImport('flarum/forum/components/NotificationGrid').then(mod => {
      const NotificationGrid = mod.default || mod;
      if (!NotificationGrid || !NotificationGrid.prototype) return;
      extend(NotificationGrid.prototype, 'notificationTypes', function (items) {
        const types = [['schedulerLessonBooked', 'fas fa-calendar-check', 'notifications.lesson_booked_label'], ['schedulerLessonRescheduled', 'fas fa-clock', 'notifications.lesson_rescheduled_label'], ['schedulerLessonCancelled', 'fas fa-ban', 'notifications.lesson_cancelled_label'], ['schedulerLessonRestored', 'fas fa-undo', 'notifications.lesson_restored_label'],
        // v0.18.0 (辉哥 10/18 拍板) — 课评的类型分组。
        //   落点是 **Flarum core 的 /notifications 页面** (铃铛「查看全部」跳那儿,
        //   NotificationGrid 是 core 组件) —— 类型 chip 加到那个页面上, **不新建 tab**。
        ['schedulerLessonReviewed', 'fas fa-star', 'notifications.lesson_reviewed_label']];
        types.forEach(_ref => {
          let [name, icon, labelKey] = _ref;
          items.add(name, {
            name,
            icon,
            label: app.translator.trans(`ziven-scheduler.forum.${labelKey}`)
          });
        });
      });
    }).catch(e => {
      console.warn('[ziven-scheduler] NotificationGrid 分组未注册 (铃铛不受影响):', e && e.message);
    });
  }, 0);
  app.routes['scheduler'] = {
    path: '/scheduler',
    component: SchedulerIndexPage
  };
  // v0.5.9 — tab 次级路由 (辉哥 9/25 11:37 拍板)
  // 选中 tab 时 URL 变化, 刷新保持当前 tab, 不跳回第一个
  // 跟 /scheduler 都用同一个 SchedulerIndexPage component, 通过 route name + URL 推断 selectedTab
  //
  // ⚠️ v0.17.80 实测踩到的坑: **漏了这个数组, tab 的 URL 会 302 回首页**。
  //   只在 `extend.php` 里加 `->route('/scheduler/mycards', ...)` 是不够的 ——
  //   那是**服务端**路由; 前端首屏是 SPA, URL 直接输进去时由 `app.routes` 决定挂哪个组件。
  //   漏注册的表现极具迷惑性: `/scheduler` 里 tab **照常显示、点进去也正常**
  //   (走的是 app.history.push, 不查这张表), 只有**刷新 / 直接输 URL** 才回首页。
  //   ⇒ 后端 extend.php 的 route 与本数组是**两处都必须改**的地方, 别只改一处。
  //
  // v0.18.45 (辉哥 10/21 拍板) — 两处改动:
  //   ① **'dashboard' 插在第一位** —— 「仪表盘」是排课中心的默认落点
  //      (辉哥硬要求: 「进入 /scheduler 默认显示仪表盘」), tab 条上也排第一。
  //      顺序与后端 `SchedulerIndexController::TAB_PERMISSIONS` 逐字同序。
  //   ② **删掉 'notifications'** —— v0.17.32 自建通知中心下线后, 后端
  //      `TAB_PERMISSIONS` 里的 'notifications' 项已删, 后端路由也已删。
  //      这个死项留在 `app.routes` 里 = 注册了一条**永远进不去**的路由
  //      (点进去后端 fail-closed 拒绝)。它是历史残留, 本轮顺手清掉。
  //      ⚠️ 只影响「直接输 /scheduler/notifications 这个 URL」这一种情况 ——
  //         正常 tab 切换根本不会产生这个 URL (buildTabList 里没有这一项)。
  const SCHEDULER_TABS = ['dashboard', 'cards', 'students', 'stats', 'calendar', 'lessons', 'sales',
  // v0.17.80 — 「我的卡片」(权限位 scheduler.viewMyCards)
  'mycards', 'rooms', 'schools', 'logs',
  // v0.18.80 (辉哥 10/24 拍板) — 「退费管理」(权限位 scheduler.viewRefunds)
  //   ⚠️ **放最后** (设计稿 E 项: 排在「操作日志」之后) ——
  //      顺序必须与后端 `SchedulerIndexController::TAB_PERMISSIONS` **逐字同序** (本仓硬约定)。
  //   ⚠️ 这一行漏了 ⇒ 直链 /scheduler/refunds 与刷新会被**302 回首页**
  //      (Flarum 路由不认识这个 path ⇒ 交给后端 → 后端 302)。
  'refunds'];
  SCHEDULER_TABS.forEach(tab => {
    app.routes['scheduler.' + tab] = {
      path: '/scheduler/' + tab,
      component: SchedulerIndexPage
    };
  });
  attachMenu();
});